import React, { Component } from 'react';
import { Form, Message } from 'semantic-ui-react';
import PaymentInformationFiservDisplay from './PaymentInformationFiservDisplay';
import PaymentInformationCheck from './PaymentInformationCheck';
import PaymentInformationCredit from './PaymentInformationCredit';
import * as cardValidator from 'card-validator';
import brnv from 'bank-routing-number-validator';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import PropTypes from 'prop-types';
import PaymentSourceDisplay from './PaymentSourceDisplay';
import styles from './PaymentSource.module.scss';
import orderBy from 'lodash.orderby';
import WalletWarningModal from './WalletWarningModal';

class PaymentSource extends Component {
	constructor(props) {
		super(props);
		const { wallets } = props.settings;
		const isRecurringAutoPolicy = props.policies ? props.policies[0].policyNumber.toLowerCase().startsWith('ppa') && props.isRecurring : false;
		const creditCardExists = wallets.some((e) => e.fundingCategory === 'Credit');

		const initialFundingAccountToken = props.source?.fiserv?.fundingAccountToken;
		this.defaultSource = {
			type: props.method || (isRecurringAutoPolicy ? 'credit' : 'check'),
			cardNumber: null,
			cardExpireMonth: null,
			cardExpireYear: null,
			nameOnCard: null,
			zipCode: null,
			securityCode: null,
			accountType: null,
			accountNumber: null,
			accountNumberConfirmation: null,
			routingNumber: null,
			bankName: null,
			fiserv: null,
			emailAddress: null,
			...props.source,
		};

		this.state = {
			errors: null,
			method: props.method || (initialFundingAccountToken ? initialFundingAccountToken : isRecurringAutoPolicy ? 'credit' : 'check'),
			addToWallet: false,
			walletNickname: null,
			walletWarningOpen: isRecurringAutoPolicy && !creditCardExists ? true : false,
			showErrors: false,
			shouldCalculateFee: false
		};

		this.frameRef = React.createRef();

		this.add = this.add.bind(this);
		this.reset = this.reset.bind(this);
		this.renderSource = this.renderSource.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onMethodChange = this.onMethodChange.bind(this);
		this.fiservMessageListener = this.fiservMessageListener.bind(this);
		this.isFiservCredit = this.isFiservCredit.bind(this);
		this.onAddToWalletChange = this.onAddToWalletChange.bind(this);
		this.onWalletNicknameChange = this.onWalletNicknameChange.bind(this);
	}	
	componentDidMount() {
		this.reset();
	}

	componentDidUpdate(prevProps) {
		const { settings, isRecurring, onIsValidUpdate, source } = this.props;

		if (isRecurring ? settings.useFiservForRecurringCreditCard : settings.useFiservForCreditCard) {
			window.addEventListener('message', this.fiservMessageListener, false);
        }

		if (source !== prevProps.source && (this.isFiservCredit(source) || ((source.fiserv !== null && source.fiserv.fundingCategory || '').toLowerCase() === 'credit' ))) {
			this.shouldCalcConvFee()
		}

		if (onIsValidUpdate) onIsValidUpdate(this.isValid());
	}

	componentWillUnmount() {
		const { settings, isRecurring } = this.props;

		if (isRecurring ? settings.useFiservForRecurringCreditCard : settings.useFiservForCreditCard) {
			window.removeEventListener('message', this.fiservMessageListener);
		}
	}

	isFiservCredit(source) {
		const { source: _source, settings, isRecurring } = this.props;
		return (source || _source).type === 'credit' && (isRecurring ? settings.useFiservForRecurringCreditCard : settings.useFiservForCreditCard);
	}
	isFiservCheck(source){
		const{source:_source,settings,isRecurring}=this.props;
		return(source || _source).type === 'check' && (isRecurring ?settings.useFiservForRecurringEFTAccount : settings.useFiservforEFTAccount)
	}

	fiservMessageListener(event) {
		const { source, onSourceUpdate, onSourceAdd, auth } = this.props;
		const { addToWallet, walletNickname } = this.state;

		if (event && event.data && event.data.Message) {
			const response = JSON.parse(event.data.Message);

			if (response.result.statusCode === 'Created') {
				const { fundingAccount } = response.fundingAccountValidationResult;
				const newSource = { ...this.defaultSource, ...source, fiserv: { ...fundingAccount, ...auth, addToWallet, walletNickname, iframeResponse: event.data.Message, fundingAccountLastFourDigits: fundingAccount.fundingAccountLastFourDigit, issuedOn: moment() } };
				onSourceUpdate(newSource);
				onSourceAdd(newSource);
			} else {
				const errors = (response.result.statusMessages || []).map((sm) => sm.description.replace(':', ' - '));
				this.setState({ errors });
			}
		}
	}

	onChange(_, data) {
		const { source, onSourceUpdate } = this.props;
		const newSource = { ...this.defaultSource, ...source, [data.name]: data.value };
		onSourceUpdate(newSource);
	}

	onMethodChange(_, { value }) {
		const { source, settings, onSourceUpdate, auth, isRecurring } = this.props;
		const { wallets } = settings;
		const { addToWallet, walletNickname } = this.state;
		if (isRecurring && value === 'credit') {
			this.setState({ walletWarningOpen: true });
			return;
		}
		if (isRecurring && value === 'check') {
			this.setState({ walletWarningOpen: true });
			return;
		}

		const _source = { ...this.defaultSource, ...source };

		if (!this.isFiservCredit(_source)) {
			this.setState({ addToWallet: false, walletNickname: '' });
		}
		
		this.setState({ method: value }, () => {
			const wallet = (wallets || []).find((w) => w.fundingAccountToken === value);
			const newSource = { ...this.defaultSource, ..._source, type: value, fiserv: { ...wallet, ...auth, addToWallet, walletNickname } };
			onSourceUpdate(newSource);
		});
	}

	async add() {
		const { settings, source, onSourceAdd, actions, policies, userEmailAddress, isWallet, userId } = this.props;

		if (this.isFiservCredit()) {
			//if (this.isFiservCredit(source)) {
			try {
				this.setState({ errors: null });
				const policyNumber = !isWallet && policies && policies.length === 1 ? (policies[0].accountNumber || policies[0].policyNumber)  : undefined;
				var { response: auth } = await actions.getPaymentAuth(policyNumber, !isWallet ? userEmailAddress : undefined, isWallet ? userId : undefined);

				const message = {
					Authorization: auth.authToken,
					APIKey: settings.fiservApiKey,
					SessionToken: auth.sessionToken,
					RequestTimeStamp: new Date(),
					MessageId: uuidv4(),
				};	

				this.frameRef.current.contentWindow.postMessage(message, '*');
			} catch {
				this.setState({ errors: [] });
			}
		} else {
			onSourceAdd(source);
		}
	}

	reset() {
		const { source, onSourceUpdate, method } = this.props;
		const _source = { ...this.defaultSource, ...source };
		const initialFundingAccountToken = _source?.fiserv?.fundingAccountToken;

		this.setState({ method: method || (initialFundingAccountToken ?? _source.type), errors: null }, () => {
			if (!initialFundingAccountToken && this.isFiservCredit(_source) && this.frameRef.current) {
				this.frameRef.current.src += '';
			}

			const newSource = { ...this.defaultSource, type: method || _source.type };
			onSourceUpdate(newSource);
		});
	}
	isValid() {
		const { source, settings, isRecurring, isWallet } = this.props;
		const { showErrors } = this.state;
		const collectSecurityCode = isRecurring ? settings.collectSecurityCodeForRecurringCreditCardPayments : settings.collectSecurityCodeForCreditCardPayments;
		const collectZipCode = isRecurring ? settings.collectZipCodeForRecurringCreditCardPayments : settings.collectZipCodeForCreditCardPayments;

		const _source = { ...this.defaultSource, ...(source || {}) };

		let valid = false;

		if (_source.fiserv?.fundingAccountToken) {
			valid = true;
		} else {
			switch (_source.type) {
				case 'credit':
					if (this.isFiservCredit(_source)) {
						valid = true;
					} else {
						const securityCodeValid = !collectSecurityCode || _source.securityCode != null;
						const zipCodeValid = !collectZipCode || _source.zipCode != null;
						valid = !!(cardValidator.number(_source.cardNumber).isValid && _source.cardExpireMonth && _source.cardExpireYear && _source.nameOnCard && securityCodeValid && zipCodeValid);
					}

					break;
				case 'check':
					valid = !!(brnv.ABARoutingNumberIsValid(_source.routingNumber) && _source.accountType && _source.accountNumber && _source.accountNumber === _source.accountNumberConfirmation && _source.routingNumber);
					if (isWallet) {
						valid = valid && _source.firstName !== '' && _source.lastName !== '';
					}
					break;
				default:
					//wallet
					valid = true;
					break;
			}
		}

		if(!showErrors) {
			this.setState({ showErrors: true });
		}
		
		return valid;
	}

	 renderSource(source) {
		const { settings, banks, actions, isRecurring, isWallet, errorsEnabled } = this.props;
		const { fiservUrl } = settings;
		const collectSecurityCode = isRecurring ? settings.collectSecurityCodeForRecurringCreditCardPayments : settings.collectSecurityCodeForCreditCardPayments;
		const collectZipCode = isRecurring ? settings.collectZipCodeForRecurringCreditCardPayments : settings.collectZipCodeForCreditCardPayments;
		const fiservEnabled = isRecurring ? settings.useFiservForRecurringCreditCard : settings.useFiservForCreditCard;
		const { showErrors } = this.state;

		if (source.fiserv?.fundingAccountToken) {
			if ((source.fiserv.fundingCategory || '').toLowerCase() === 'credit') {
				return (
					<Form.Field> {this.state.shouldCalculateFee && !isRecurring &&
						<div className="inline required field">
							<label className={styles.requiredLbl}> </label><p className={styles.displayInline}> <b>Using a credit card now includes a convenience fee charged by our 3rd party credit card processor. The fee will be included in your total payment. To avoid paying this fee we also accept ACH/eCheck payments from your bank account. </b> </p>
							</div>
					}
						<label>Selected Method</label>
						<PaymentInformationFiservDisplay source={source} />
					</Form.Field>
				);
			}
			else {
				return (
					<Form.Field>
						<label>Selected Method</label>
						<PaymentInformationFiservDisplay source={source} />
					</Form.Field>
				);
			}
		}

		switch (source.type) {
			case 'credit':
				if (fiservEnabled) {
					return <div>{this.state.shouldCalculateFee && <div className="inline required field">
						<label className={styles.requiredLbl}> </label><p className={styles.displayInline}> <b>Using a credit card now includes a convenience fee charged by our 3rd party credit card processor. The fee will be included in your total payment. To avoid paying this fee we also accept ACH/eCheck payments from your bank account. </b> </p>
						</div>}
						<iframe id='billingiFrame' ref={this.frameRef} title='payment' src={fiservUrl} scrolling='auto' />
						</div>
								
				} else {
					return <div>{this.state.shouldCalculateFee && <div className="inline required field"> <label className={styles.requiredLbl}> </label><p className={styles.displayInline}> <b>Using a credit card now includes a convenience fee charged by our 3rd party credit card processor. The fee will be included in your total payment. To avoid paying this fee we also accept ACH/eCheck payments from your bank account. </b> </p></div>}
						<PaymentInformationCredit source={source} onChange={this.onChange} collectSecurityCode={collectSecurityCode} collectZipCode={collectZipCode} showErrors={showErrors && errorsEnabled} /> </div>;
				}
			case 'check':
				return <PaymentInformationCheck source={source} onChange={this.onChange} banks={banks} actions={actions} isWallet={isWallet} showErrors={showErrors && errorsEnabled} />;
			default:
				return (
					<Form.Field>
						<label>Selected Method</label>
						<PaymentInformationFiservDisplay source={source} />
					</Form.Field>
				);
		}
	}

	onAddToWalletChange(_, { checked }) {
		const { source } = this.props;
		const _source = { ...this.defaultSource, ...source };

		this.setState({ addToWallet: checked });

		if (_source.fiserv) {
			_source.fiserv.addToWallet = checked;
		}
	}

	onWalletNicknameChange(_, { value }) {
		const { source } = this.props;
		const _source = { ...this.defaultSource, ...source };

		this.setState({ walletNickname: value });

		if (_source.fiserv) {
			_source.fiserv.walletNickname = value;
		}
	}

	async shouldCalcConvFee() {
		try {
			const calculateFeeResponse  = await this.props.actions.shouldCalculateConvienceFee(); //IF this is false then UI wont show ConvFee and dont cal below method, also this logic will come for creditcard and existing creditcard wallet
			console.log(calculateFeeResponse.response);
			const { shouldCalculateFee } = calculateFeeResponse.response;
			this.setState({shouldCalculateFee});
		} catch (err) {
			console.log(err)
		}
	}

	render() {
		const { settings, source, isRecurring, policies, method: lockedMethod } = this.props;
		const { wallets, allowUseOfWalletItems } = settings;
		//const { method, addToWallet, walletNickname, errors, walletWarningOpen } = this.state;
		const { method, errors, walletWarningOpen } = this.state;
		const _source = { ...this.defaultSource, ...source };
		const isRecurringAutoPolicy = policies ? policies[0].policyNumber.toLowerCase().startsWith('ppa') && isRecurring : false;

		const _policyNumber = policies ? policies[0].policyNumber : '';
		const _paymentPlan = policies ? policies[0].paymentPlan ?? '' : ''; // 31 - Renewal Credit Card Monthly,  32- Renewal EFT Monthly
		const _fundingCategory = wallets.find(wl => wl.inUseByPolicies.some(p => p === _policyNumber))?.fundingCategory ?? _paymentPlan.toLowerCase().includes('credit card') ? 'credit' : 'check';
		let methods = [];
		const restrictCardsForRecurringPayments = !settings.restrictCreditCardsForRecurringPayments_Date ? false : new Date() >= new Date(settings.restrictCreditCardsForRecurringPayments_Date);

		if ((!lockedMethod) || (lockedMethod && lockedMethod === 'check')) {
			methods.push({ key: 'check', value: 'check', text: allowUseOfWalletItems ? 'New Bank Account' : 'Bank Account' });
		}

		if ((!lockedMethod || (lockedMethod && lockedMethod === 'credit')) && (restrictCardsForRecurringPayments ?
			(!isRecurring || (this.props.hasRecurringPaymentSetup && (_fundingCategory.toLowerCase() === 'credit' || _fundingCategory.toLowerCase() === 'debit'))): true)) {
			methods.push({ key: 'credit', value: 'credit', text: allowUseOfWalletItems ? 'New Credit/Debit Card' : 'Credit/Debit Card' });
		}
		
		if (allowUseOfWalletItems) {
			methods = methods.concat(
				orderBy(
					(wallets || [])
						//.filter((w) => (isRecurring ? (this.props.hasRecurringPaymentSetup ? ((_fundingCategory.toLowerCase() === 'credit' || _fundingCategory.toLowerCase() === 'debit') ? true : w.fundingCategory.toLowerCase() === 'check') : (w.fundingCategory !== 'Credit' && w.fundingCategory !== 'Debit')) : true))
						.filter((w) => restrictCardsForRecurringPayments ? (isRecurring ?
							(this.props.hasRecurringPaymentSetup ?
								((_fundingCategory.toLowerCase() === 'credit' || _fundingCategory.toLowerCase() === 'debit') ? true : w.fundingCategory.toLowerCase() === 'check')
								: (w.fundingCategory !== 'Credit' && w.fundingCategory !== 'Debit'))
							: true): true)
						.map((w) => {
							return {
								key: w.fundingAccountToken,
								value: w.fundingAccountToken,
								text: w.nickname,
								wallet: w,
								content: (
									<div>
										<div className={styles.methodItemText}>{w.nickname}</div>
										<div>
											<PaymentSourceDisplay source={{ ...w, fiserv: w }} />
										</div>
									</div>
								),
							};
						}),
					(w) => w.nickname
				) 
			);
		}

		return (
			<React.Fragment>
				<WalletWarningModal open={walletWarningOpen} onClose={() => this.setState({ ...this.state, walletWarningOpen: false })} />
				<Form.Group>
					<Form.Dropdown disabled={methods.length === 1 || isRecurringAutoPolicy} inline name='method' label='Method' value={method} onChange={this.onMethodChange} required scrolling options={methods} />
				</Form.Group>
				{errors && (
					<Message error visible>
						Failed to process payment. Please try again.
						<ul>
							{errors.map((e, i) => {
								return <li key={i}>{e}</li>;
							})}
						</ul>
					</Message>
				)}
				{this.renderSource(_source)}
				{/* {allowUseOfWalletItems && this.isFiservCredit(_source) && !isRecurring && (
					<React.Fragment>
						<Form.Checkbox inline name='addToWallet' label='Add to Wallet?' checked={addToWallet} onChange={this.onAddToWalletChange} />
						{addToWallet && <Form.Input name='walletNickname' label='Wallet Nickname' onChange={this.onWalletNicknameChange} value={walletNickname || ''} />}
					</React.Fragment>
				)} */}
			</React.Fragment>
		);
	}
}

PaymentSource.propTypes = {
	source: PropTypes.object,
	settings: PropTypes.object,
	banks: PropTypes.object,
	auth: PropTypes.object,
	onSourceUpdate: PropTypes.func,
	onSourceAdd: PropTypes.func,
	actions: PropTypes.object.isRequired,
	isRecurring: PropTypes.bool,
	userEmailAddress: PropTypes.string,
};

export default PaymentSource;
